import { useState, useEffect } from "react";
import { isNative } from "../utils/nativePush";

function readSize() {
  return { width: window.innerWidth, height: window.innerHeight };
}

// Subscribes to viewport size. Folding/unfolding an iPhone Duo fires
// resize (and orientationchange) in WKWebView, so this re-renders on both.
function useWindowSize() {
  const [size, setSize] = useState(readSize);
  useEffect(() => {
    const handler = () => setSize(prev => {
      const next = readSize();
      return prev.width === next.width && prev.height === next.height ? prev : next;
    });
    window.addEventListener("resize", handler);
    window.addEventListener("orientationchange", handler);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("orientationchange", handler);
    };
  }, []);
  return size;
}

// Layout modes:
//   isWide  — desktop-style cards and centered modals. Width >= 768, or a
//             native device whose screen is large in both directions (iPhone Duo
//             unfolded portrait ~626x890pt, iPad).
//   isSplit — list + detail. Native, landscape-ish and tall enough: iPhone Duo
//             unfolded landscape (~951x669pt) and iPad. Excludes iPhone landscape
//             (height <= 440), the folded Duo, and desktop web.
//   isLargeScreen — native device large in both directions, in either orientation:
//             iPhone Duo unfolded, iPad. Never a regular iPhone (even in landscape).
function computeLayout(width, height, native) {
  const isLargeScreen = native && Math.min(width, height) >= 600;
  const isSplit = native && width >= 840 && height >= 560;
  const isWide  = width >= 768 || isLargeScreen;
  return { width, height, isWide, isSplit, isLargeScreen };
}

function useLayout() {
  const { width, height } = useWindowSize();
  return computeLayout(width, height, isNative());
}

function useWindowWidth() {
  return useWindowSize().width;
}

export { useWindowWidth, useWindowSize, useLayout, computeLayout };
